import React, { useState, useEffect } from 'react';
import { Card, Progress, Space, Typography, Statistic, Row, Col, Tag, Timeline } from 'antd';
import { 
  RobotOutlined, 
  TrophyOutlined, 
  RiseOutlined, 
  ExperimentOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  BookOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

interface LearningMetric {
  name: string;
  current: number;
  target: number;
  status: 'improving' | 'stable' | 'declining';
  lastUpdate: string;
}

interface ModelProgress {
  id: string;
  name: string;
  accuracy: number;
  learningRate: number;
  trainingProgress: number;
  status: 'training' | 'deployed' | 'testing';
  samples: number;
}

export const AILearningProgress: React.FC = () => {
  const [overallIntelligence, setOverallIntelligence] = useState(78.5);
  const [learningMetrics, setLearningMetrics] = useState<LearningMetric[]>([]);
  const [models, setModels] = useState<ModelProgress[]>([]);
  const [recentLearning, setRecentLearning] = useState<string[]>([]);

  useEffect(() => {
    // Simulate AI learning progress
    const interval = setInterval(() => {
      setOverallIntelligence(prev => {
        const change = (Math.random() - 0.5) * 0.5;
        return Math.min(100, Math.max(0, prev + change));
      });

      // Add new learning events
      if (Math.random() > 0.7) {
        const learningEvents = [
          'Pattern recognition accuracy improved by 0.3%',
          'New threat vector identified and classified',
          'Behavioral anomaly detection enhanced',
          'Emergency response prediction optimized',
          'False positive rate reduced by 0.1%',
          'Cross-correlation analysis strengthened'
        ];
        
        const newEvent = learningEvents[Math.floor(Math.random() * learningEvents.length)];
        setRecentLearning(prev => [newEvent, ...prev.slice(0, 4)]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Initialize data
  useEffect(() => {
    setLearningMetrics([
      {
        name: 'Threat Detection',
        current: 94.7,
        target: 98.0,
        status: 'improving',
        lastUpdate: '2 min ago'
      },
      {
        name: 'Pattern Recognition',
        current: 91.3,
        target: 95.0,
        status: 'improving',
        lastUpdate: '5 min ago'
      },
      {
        name: 'Behavioral Analysis',
        current: 87.6,
        target: 92.0,
        status: 'stable',
        lastUpdate: '8 min ago'
      },
      {
        name: 'Prediction Accuracy',
        current: 89.4,
        target: 93.0,
        status: 'improving',
        lastUpdate: '3 min ago'
      }
    ]);

    setModels([
      {
        id: 'threat-detector',
        name: 'Threat Detector',
        accuracy: 94.7,
        learningRate: 0.001,
        trainingProgress: 85,
        status: 'training',
        samples: 1247000
      },
      {
        id: 'behavior-analyzer',
        name: 'Behavior Analyzer',
        accuracy: 91.3,
        learningRate: 0.0008,
        trainingProgress: 100,
        status: 'deployed',
        samples: 892000
      },
      {
        id: 'pattern-finder',
        name: 'Pattern Finder',
        accuracy: 87.6,
        learningRate: 0.0012,
        trainingProgress: 67,
        status: 'training',
        samples: 654000
      }
    ]);

    setRecentLearning([
      'Threat detection model updated with new data',
      'Behavioral pattern recognition improved',
      'Emergency response prediction enhanced',
      'False positive rate optimized'
    ]);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'improving': return '#52c41a';
      case 'stable': return '#1890ff';
      case 'declining': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getModelStatusColor = (status: string) => {
    switch (status) {
      case 'training': return 'orange';
      case 'deployed': return 'green';
      case 'testing': return 'blue';
      default: return 'default';
    }
  };

  return (
    <Card 
      title={
        <Space>
          <RobotOutlined style={{ color: '#722ed1' }} />
          <span>🧠 AI Öğrenme İlerlemesi</span>
        </Space>
      }
      style={{ height: '600px' }}
    >
      {/* Overall Intelligence Score */}
      <div style={{ textAlign: 'center', marginBottom: 20, padding: 16, background: '#f9f0ff', borderRadius: 6 }}>
        <Space direction="vertical" size={0}>
          <Text type="secondary">Genel AI Zekası</Text>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#722ed1' }}>
            {overallIntelligence.toFixed(1)}%
          </div>
          <Progress 
            percent={overallIntelligence} 
            strokeColor={{
              '0%': '#722ed1',
              '100%': '#b37feb',
            }}
            size="small"
            showInfo={false}
          />
        </Space>
      </div>

      {/* Learning Metrics - Kompakt */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: '14px' }}>📊 Performans Metrikleri</Text>
        <div style={{ marginTop: 8 }}>
          {learningMetrics.slice(0, 3).map((metric, index) => (
            <div key={index} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Text style={{ fontSize: '11px' }}>{metric.name}</Text>
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  {metric.current}%
                </Text>
              </div>
              <Progress 
                percent={(metric.current / metric.target) * 100} 
                size="small"
                strokeColor={getStatusColor(metric.status)}
                showInfo={false}
                style={{ marginBottom: 0 }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Active Models - Kompakt */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: '14px' }}>🤖 Aktif Modeller</Text>
        <div style={{ marginTop: 8 }}>
          {models.slice(0, 2).map((model, index) => (
            <div key={model.id} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '4px 0',
              borderBottom: index < 1 ? '1px solid #f0f0f0' : undefined
            }}>
              <Text style={{ fontSize: '11px' }}>{model.name}</Text>
              <Text style={{ fontSize: '10px', color: '#666' }}>
                {model.accuracy}%
              </Text>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Learning Events */}
      <div>
        <Text strong style={{ fontSize: '14px' }}>🎓 Son Öğrenmeler</Text>
        <div style={{ 
          height: '120px', 
          overflowY: 'auto', 
          marginTop: 8,
          fontSize: '11px'
        }}>
          {recentLearning.slice(0, 5).map((event, index) => (
            <div key={index} style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              marginBottom: 6,
              padding: '2px 0'
            }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6, fontSize: '10px', marginTop: 2 }} />
              <Text style={{ fontSize: '10px', color: '#666', lineHeight: '1.3' }}>{event}</Text>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
